import { useState } from "react";
import api from "@/lib/api";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { MoreHorizontal, ExternalLink, Edit3, Archive, Trash2 } from "lucide-react";
import { toast } from "sonner";

const COPY = {
  archive: { title: "Archive this job?", body: "It moves to Archived Jobs and is hidden from day-to-day screens. You can restore it any time.", cta: "Archive" },
  delete: { title: "Delete this job permanently?", body: "This removes the job and its job chat. This cannot be undone — archive instead if you might need it again.", cta: "Delete" },
};

export function JobActions({ job, ctx }) {
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      if (confirm === "archive") await api.post(`/jobs/${job.id}/archive`);
      else await api.delete(`/jobs/${job.id}`);
      toast.success(confirm === "archive" ? "Job archived" : "Job deleted");
      setConfirm(null);
      ctx.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className="jr-dots" aria-label={`Actions for ${job.title}`} data-testid={`jr-actions-${job.id}`}><MoreHorizontal className="w-5 h-5" /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => ctx.navigate(`/app/jobs/${encodeURIComponent(job.id)}`)} data-testid={`jr-action-open-${job.id}`}><ExternalLink className="w-4 h-4 mr-2" />Open job</DropdownMenuItem>
          {ctx.manage && <>
            <DropdownMenuItem onSelect={() => ctx.edit(job)} data-testid={`jr-action-edit-${job.id}`}><Edit3 className="w-4 h-4 mr-2" />Edit job</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => setConfirm("archive")} data-testid={`jr-action-archive-${job.id}`}><Archive className="w-4 h-4 mr-2" />Archive job</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setConfirm("delete")} className="text-red-600 focus:text-red-600" data-testid={`jr-action-delete-${job.id}`}><Trash2 className="w-4 h-4 mr-2" />Delete job</DropdownMenuItem>
          </>}
        </DropdownMenuContent>
      </DropdownMenu>
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && !busy && setConfirm(null)}>
        <AlertDialogContent data-testid="jr-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{COPY[confirm]?.title}</AlertDialogTitle>
            <AlertDialogDescription>{job.job_number ? `${job.job_number} · ` : ""}{job.title}. {COPY[confirm]?.body}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="jr-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={(e) => { e.preventDefault(); run(); }} className={confirm === "delete" ? "bg-red-600 hover:bg-red-700" : "bg-[#FF5F15] hover:bg-[#E65100]"} data-testid="jr-confirm-ok">{busy ? "Working…" : COPY[confirm]?.cta}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </span>
  );
}
